import { checkChannelStatus, connectOutlook, createChannel, deleteChannel, getMyChannels, reconnectChannel, unipileConnect, verifyChannel } from "../features/settings/api/Integration";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  showSuccess,
  showErrorFromMutation as reportMutationError,
} from "@/shared/utils/toast";

// create LinkedIn channel hook
export const useCreateChannel = () => {
  return useMutation({
    mutationFn: createChannel,
    onError: (error) => {
      const data = error.response?.data;
      reportMutationError(
        error,
        data?.message || data?.error || "Failed to create channel. Please refresh the page.",
      );
    },
  });
};

// verify LinkedIn channel OTP hook
export const useVerifyChannel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: verifyChannel,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["my-channels"] });
      if (data?.status === "Active" || data?.success === true) {
        showSuccess(data.message || "Channel Integration Successfull");
      }
    },
    onError: (error) => {
      reportMutationError(
        error,
        error.response?.data?.message || "Failed to verify channel. Please refresh the page.",
      );
    },
  });
};

// check channel status hook
export const useCheckChannelStatus = (accountId, enabled) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["channel-status", accountId],
    queryFn: () => checkChannelStatus(accountId),
    enabled: !!accountId && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.result?.status;

      if (status === "Active") {
        queryClient.invalidateQueries({ queryKey: ["my-channels"] });
        return false;
      }
      if (status === "Failed" || status === "Expired") {
        return false;
      }
      return 5000;
    },
  });
};

// get my channels hook
export const useGetMyChannels = () => {
  return useQuery({
    queryKey: ["my-channels"],
    queryFn: getMyChannels,
    onError: (error) => {
      reportMutationError(
        error,
        error.response?.data?.message ||
          "Failed to get my channels. Please refresh the page.",
      );
    },
  });
};

// delete channel hook
export const useDeleteChannel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteChannel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-channels"] });
      showSuccess("Account disconnected successfully!");
    },
    onError: (error) => {
      reportMutationError(
        error,
        error.response?.data?.message || "Failed to disconnect account. Please refresh the page.",
      );
    },
  });
};

// reconnect channel hook - get hosted auth URL
export const useReconnectChannel = () => {
  return useMutation({
    mutationFn: reconnectChannel,
    onError: (error) => {
      reportMutationError(
        error,
        error.response?.data?.message || "Failed to start reconnect. Please refresh the page.",
      );
    },
  });
};

// connect outlook hook - get auth URL
export const useConnectOutlook = () => {
  return useMutation({
    mutationFn: connectOutlook,
    onError: (error) => {
      reportMutationError(
        error,
        error.response?.data?.message || "Failed to connect outlook. Please refresh the page.",
      );
    },
  });
};

// unipile connect hook
export const useUnipileConnect = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId }) => unipileConnect({ accountId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-channels"] });
      showSuccess("Account connected successfully!");
    },
    onError: (error) => {
      reportMutationError(
        error,
        error.response?.data?.message || error.message || "Failed to complete Outlook connection.",
      );
    },
  });
};
